import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CgSpinner } from 'react-icons/cg';
import { FaArrowLeft, FaCog, FaEnvelope } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { userAppData } from '../MainApp.js';
import Header from '../common/header.js';
import {
  getReproStaffPermissions,
  updateReproStaffPermission,
} from './sysManagerReproApi.js';

import './SysManagerReprographics.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
const ACCESS_FILTERS = [
  {
    value: 'all',
    label: 'All staff',
    description: 'Show every active staff account.',
  },
  {
    value: 'enabled',
    label: 'Access enabled',
    description: 'Show staff who can open Reprographics.',
  },
  {
    value: 'disabled',
    label: 'No access',
    description: 'Show staff without Reprographics access.',
  },
];

/** Return a single initial for the profile-image placeholder. */
function getAvatarInitial(displayName) {
  const cleanName = String(displayName || '').trim();
  return cleanName ? cleanName.charAt(0).toUpperCase() : '?';
}

/** Normalise the three permission values returned by the API. */
function normalisePermissions(rawPermissions) {
  return {
    perms: Number(rawPermissions?.perms ?? 0) === 1 ? 1 : 0,
    allowManageRequests: Boolean(rawPermissions?.allowManageRequests),
    restrictManageRequestsToOwn: Boolean(rawPermissions?.restrictManageRequestsToOwn),
  };
}

/** Normalise the assignment settings stored in SystemOptions. */
function normaliseAssignmentSettings(rawSettings) {
  return {
    autoAssign: Boolean(rawSettings?.autoAssign),
    notify: Boolean(rawSettings?.notify),
  };
}

/** Render a labelled switch using the shared System Manager visual direction. */
function PermissionToggle({ label, description, checked, disabled, saving = false, variant = '', onChange }) {
  return (
    <label
      className={`sysManager_repro_toggle${checked ? ' sysManager_repro_toggle--active' : ''}${disabled ? ' sysManager_repro_toggle--disabled' : ''}${variant ? ` sysManager_repro_toggle--${variant}` : ''}`}
      aria-busy={saving}
    >
      <input
        type='checkbox'
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span
        className={`sysManager_repro_toggleTrack${checked ? ' sysManager_repro_toggleTrack--active' : ''}${saving ? ' sysManager_repro_toggleTrack--saving' : ''}`}
        aria-hidden='true'
      >
        {saving ? (
          <CgSpinner className='sysManager_repro_toggleSpinner' />
        ) : (
          <span className='sysManager_repro_toggleKnob' />
        )}
      </span>
      <span className='sysManager_repro_toggleText'>
        <strong>{label}</strong>
        <small>{description}</small>
      </span>
    </label>
  );
}

/** Manage Reprographics module access for every active staff account. */
export default function SysManagerReprographics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [staffItems, setStaffItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [accessFilter, setAccessFilter] = useState('all');
  const [managersOnly, setManagersOnly] = useState(false);
  const [updatingCogIds, setUpdatingCogIds] = useState({});
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);

  const filteredStaff = useMemo(() => {
    const cleanSearchText = searchText.trim().toLowerCase();
    return staffItems.filter((staffMember) => {
      const displayName = String(staffMember?.displayName || '').toLowerCase();
      const permissions = normalisePermissions(staffMember?.reproPermissions);
      const hasAccess = permissions.perms === 1;

      if (cleanSearchText && !displayName.includes(cleanSearchText)) {
        return false;
      }
      if (accessFilter === 'enabled' && !hasAccess) {
        return false;
      }
      if (accessFilter === 'disabled' && hasAccess) {
        return false;
      }
      if (managersOnly && !permissions.allowManageRequests) {
        return false;
      }
      return true;
    });
  }, [accessFilter, managersOnly, searchText, staffItems]);

  async function loadStaffPermissions() {
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await getReproStaffPermissions();
      setStaffItems(response.items.map((item) => ({
        ...item,
        reproPermissions: normalisePermissions(item.reproPermissions),
        reproAssignmentSettings: normaliseAssignmentSettings(item.reproAssignmentSettings),
      })));
    } catch (error) {
      console.error('Failed to load Reprographics staff permissions', error);
      setLoadFailed(true);
      setStaffItems([]);
      toast.error('❌ Failed to load Reprographics staff permissions.', TOAST_CONFIG);
    } finally {
      setLoading(false);
    }
  }

  async function handleAssignmentChange(staffMember, setting, value) {
    const targetCogId = String(staffMember?.cogId || '').trim();
    const displayName = String(staffMember?.displayName || 'this staff member').trim();
    if (!targetCogId || updatingCogIds[targetCogId]) {
      return;
    }

    setUpdatingCogIds((currentValue) => ({ ...currentValue, [targetCogId]: setting }));
    try {
      const response = await updateReproStaffPermission({
        targetCogId,
        permission: setting,
        value: Boolean(value),
      });
      const savedSettings = normaliseAssignmentSettings(response.reproAssignmentSettings);
      setStaffItems((currentItems) => currentItems.map((item) => {
        const currentSettings = normaliseAssignmentSettings(item.reproAssignmentSettings);
        if (item.cogId === targetCogId) {
          return { ...item, reproAssignmentSettings: savedSettings };
        }
        if (setting === 'autoAssign' && value) {
          return {
            ...item,
            reproAssignmentSettings: { ...currentSettings, autoAssign: false },
          };
        }
        return item;
      }));
      toast.success(`✅ Updated assignment settings for ${displayName}.`, TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to update Reprographics assignment settings', error);
      toast.error(`❌ Failed to update assignment settings for ${displayName}.`, TOAST_CONFIG);
    } finally {
      setUpdatingCogIds((currentValue) => {
        const nextValue = { ...currentValue };
        delete nextValue[targetCogId];
        return nextValue;
      });
    }
  }

  const reproManagers = staffItems.filter((staffMember) => {
    const permissions = normalisePermissions(staffMember.reproPermissions);
    return permissions.perms === 1 && permissions.allowManageRequests;
  });
  const automaticAssignee = reproManagers.find((staffMember) => (
    normaliseAssignmentSettings(staffMember.reproAssignmentSettings).autoAssign
  ));

  useEffect(() => {
    const sysManagerPermissions = userAppData?.sysManager || {};
    if (
      Number(sysManagerPermissions?.perms ?? 0) !== 1
      || sysManagerPermissions?.reproAdmin !== true
    ) {
      navigate('/app');
      return;
    }
    loadStaffPermissions();
  }, [navigate]);

  async function handlePermissionChange(staffMember, permission, value) {
    const targetCogId = String(staffMember?.cogId || '').trim();
    const displayName = String(staffMember?.displayName || 'this staff member').trim();
    if (!targetCogId || updatingCogIds[targetCogId]) {
      return;
    }

    setUpdatingCogIds((currentValue) => ({ ...currentValue, [targetCogId]: permission }));
    try {
      const response = await updateReproStaffPermission({
        targetCogId,
        permission,
        value: Boolean(value),
      });
      const savedPermissions = normalisePermissions(response.reproPermissions);
      const savedAssignmentSettings = normaliseAssignmentSettings(
        response.reproAssignmentSettings,
      );
      setStaffItems((currentItems) => currentItems.map((item) => (
        item.cogId === targetCogId
          ? {
            ...item,
            reproPermissions: savedPermissions,
            reproAssignmentSettings: savedAssignmentSettings,
          }
          : item
      )));
      toast.success(`✅ Updated Reprographics access for ${displayName}.`, TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to update Reprographics staff permissions', error);
      toast.error(`❌ Failed to update Reprographics access for ${displayName}.`, TOAST_CONFIG);
    } finally {
      setUpdatingCogIds((currentValue) => {
        const nextValue = { ...currentValue };
        delete nextValue[targetCogId];
        return nextValue;
      });
    }
  }

  return (
    <div className='main_div'>
      <div className='internal_main_div sysManager_repro_container'>
        <Header
          title='Reprographics Access'
          subtitle='Control who can submit, manage, and receive assigned Reprographics requests.'
          rightContent={(<div className='sysManager_repro_headerActions'>
            <button className='module-page-header__button' type='button' onClick={() => setAssignmentModalOpen(true)}>
              <FaCog aria-hidden='true' />
              Assignment &amp; emails
            </button>
            <button className='module-page-header__button' type='button' onClick={() => navigate('/app/sysmanager')}>
              <FaArrowLeft aria-hidden='true' />
              Back to System Manager
            </button>
          </div>)}
        />

        <section className='sysManager_repro_accessSurface'>
          <div className='sysManager_repro_accessBody'>
            <aside className='sysManager_repro_filters'>
              <div className='sysManager_repro_filtersCard'>
                <h2>Filters</h2>
                <label
                  className={`sysManager_repro_filterToggle${managersOnly ? ' sysManager_repro_filterToggle--active' : ''}`}
                >
                  <input
                    type='checkbox'
                    checked={managersOnly}
                    onChange={(event) => setManagersOnly(event.target.checked)}
                  />
                  <span
                    className={`sysManager_repro_toggleTrack${managersOnly ? ' sysManager_repro_toggleTrack--active' : ''}`}
                    aria-hidden='true'
                  >
                    <span className='sysManager_repro_toggleKnob' />
                  </span>
                  <span>
                    <strong>Managers only</strong>
                    <small>Only show staff who can manage requests.</small>
                  </span>
                </label>

                <div className='sysManager_repro_filterList'>
                  {ACCESS_FILTERS.map((option) => (
                    <label className='sysManager_repro_filterOption' key={option.value}>
                      <span className='sysManager_repro_filterControl'>
                        <input
                          type='radio'
                          name='sysmanager-repro-access-filter'
                          value={option.value}
                          checked={accessFilter === option.value}
                          onChange={(event) => setAccessFilter(event.target.value)}
                        />
                        <strong>{option.label}</strong>
                      </span>
                      <small>{option.description}</small>
                    </label>
                  ))}
                </div>
              </div>
            </aside>

            <div className='sysManager_repro_accessMain'>
              <div className='sysManager_repro_toolbar'>
                <input
                  type='search'
                  className='sysManager_repro_search'
                  value={searchText}
                  placeholder='Search staff...'
                  aria-label='Search staff'
                  onChange={(event) => setSearchText(event.target.value)}
                />
              </div>

              {loading ? (
                <div className='sysManager_repro_loading'>
                  <CgSpinner className='sysManager_repro_spinner' />
                  <p>Loading staff permissions...</p>
                </div>
              ) : loadFailed ? (
                <div className='sysManager_repro_emptyState'>
                  <p>Staff permissions could not be loaded.</p>
                  <button type='button' onClick={loadStaffPermissions}>Try again</button>
                </div>
              ) : filteredStaff.length === 0 ? (
                <div className='sysManager_repro_emptyState'>
                  No staff matched the current search and filters.
                </div>
              ) : (
                <div className='sysManager_repro_tableWrap'>
                  <table className='sysManager_repro_table'>
                    <thead>
                      <tr>
                        <th>Staff member</th>
                        <th>Reprographics permissions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStaff.map((staffMember) => {
                        const targetCogId = String(staffMember?.cogId || '').trim();
                        const permissions = normalisePermissions(staffMember?.reproPermissions);
                        const hasAccess = permissions.perms === 1;
                        const isManager = hasAccess && permissions.allowManageRequests;
                        const updatingPermission = updatingCogIds[targetCogId] || '';
                        const isUpdating = Boolean(updatingPermission);

                        return (
                          <tr key={targetCogId}>
                            <td>
                              <div className='sysManager_repro_userCell'>
                                <div className='sysManager_repro_avatarWrap'>
                                  <div className='sysManager_repro_avatarPlaceholder'>
                                    {getAvatarInitial(staffMember?.displayName)}
                                  </div>
                                  {staffMember?.profileImageUrl && (
                                    <img
                                      className='sysManager_repro_avatar'
                                      src={staffMember.profileImageUrl}
                                      alt=''
                                      onError={(event) => {
                                        event.currentTarget.style.opacity = '0';
                                      }}
                                    />
                                  )}
                                </div>
                                <strong>{staffMember?.displayName || 'Unknown user'}</strong>
                              </div>
                            </td>
                            <td>
                              <div className='sysManager_repro_permissionControls'>
                                <PermissionToggle
                                  label='Reprographics Access'
                                  description='Can open the module and submit requests.'
                                  checked={hasAccess}
                                  disabled={isUpdating}
                                  saving={updatingPermission === 'perms'}
                                  onChange={(value) => handlePermissionChange(
                                    staffMember,
                                    'perms',
                                    value,
                                  )}
                                />

                                {hasAccess && (
                                  <div className={`sysManager_repro_manageGroup${isManager ? ' sysManager_repro_manageGroup--active' : ''}`}>
                                    <PermissionToggle
                                      label='Manage Requests'
                                      description='Can use the manager request workflow.'
                                      checked={isManager}
                                      disabled={isUpdating}
                                      saving={updatingPermission === 'allowManageRequests'}
                                      variant='groupPrimary'
                                      onChange={(value) => handlePermissionChange(
                                        staffMember,
                                        'allowManageRequests',
                                        value,
                                      )}
                                    />

                                    {isManager && (
                                      <div className='sysManager_repro_manageRestriction'>
                                        <PermissionToggle
                                          label='Restrict to Assigned Requests'
                                          description='Can only see requests assigned to this staff member.'
                                          checked={permissions.restrictManageRequestsToOwn}
                                          disabled={isUpdating}
                                          saving={updatingPermission === 'restrictManageRequestsToOwn'}
                                          variant='nested'
                                          onChange={(value) => handlePermissionChange(
                                            staffMember,
                                            'restrictManageRequestsToOwn',
                                            value,
                                          )}
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </section>

        {assignmentModalOpen && (
          <div className='sysManager_repro_modalBackdrop' role='presentation' onMouseDown={() => setAssignmentModalOpen(false)}>
            <section
              className='sysManager_repro_modal'
              role='dialog'
              aria-modal='true'
              aria-labelledby='repro-assignment-modal-title'
              onMouseDown={(event) => event.stopPropagation()}
            >
              <button
                type='button'
                className='sysManager_repro_modalClose'
                aria-label='Close assignment and email settings'
                onClick={() => setAssignmentModalOpen(false)}
              >×</button>
              <div className='sysManager_repro_modalHeading'>
                <FaCog aria-hidden='true' />
                <div>
                  <h2 id='repro-assignment-modal-title'>Assignment &amp; email settings</h2>
                  <p>Managers are automatically available for request assignment. Choose one automatic assignee and who receives request notification emails.</p>
                </div>
              </div>

              {reproManagers.length === 0 ? (
                <div className='sysManager_repro_modalEmpty'>Enable Manage Requests for at least one staff member first.</div>
              ) : (
                <div className='sysManager_repro_settingsList'>
                  <label className='sysManager_repro_noAutoAssign'>
                    <input
                      type='radio'
                      name='repro-auto-assignee'
                      checked={!automaticAssignee}
                      disabled={!automaticAssignee || Boolean(updatingCogIds[automaticAssignee.cogId])}
                      onChange={() => handleAssignmentChange(automaticAssignee, 'autoAssign', false)}
                    />
                    <span>
                      <strong>Leave new requests unassigned</strong>
                      <small>Managers can manually assign requests from the Reprographics queue.</small>
                    </span>
                  </label>
                  {reproManagers.map((staffMember) => {
                    const settings = normaliseAssignmentSettings(staffMember.reproAssignmentSettings);
                    const savingSetting = updatingCogIds[staffMember.cogId] || '';
                    return (
                      <article className='sysManager_repro_settingsRow' key={staffMember.cogId}>
                        <div className='sysManager_repro_settingsPerson'>
                          <strong>{staffMember.displayName}</strong>
                          <small>Assignable Reprographics manager</small>
                        </div>
                        <label className='sysManager_repro_settingChoice'>
                          <input
                            type='radio'
                            name='repro-auto-assignee'
                            checked={settings.autoAssign}
                            disabled={Boolean(savingSetting)}
                            onChange={() => handleAssignmentChange(staffMember, 'autoAssign', true)}
                          />
                          <span>
                            <strong>Auto assign</strong>
                            <small>Send every new request to this person.</small>
                          </span>
                        </label>
                        <PermissionToggle
                          label='Email notifications'
                          description='Receive manager emails about Reprographics requests.'
                          checked={settings.notify}
                          disabled={Boolean(savingSetting)}
                          saving={savingSetting === 'notify'}
                          onChange={(value) => handleAssignmentChange(staffMember, 'notify', value)}
                        />
                      </article>
                    );
                  })}
                </div>
              )}
              <div className='sysManager_repro_modalNote'>
                <FaEnvelope aria-hidden='true' />
                Automatic assignees receive assignment emails. Notification emails are for other selected managers.
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
